import React, { useEffect, useState } from "react";
import { FileText, Link2, MapPin, ExternalLink, Search, Award, PlayCircle, CheckCircle2, Crosshair, Footprints, Radio, HeartPulse, ShieldAlert, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const yt = (q) => `https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`;
const TC_3_21_75 = "https://irp.fas.org/doddir/army/tc3-21-75.pdf";
const STP_21_1 = "https://asktop.net/wp/download/10/stp21_1.pdf";
const FM_LAND_NAV = "https://irp.fas.org/doddir/army/fm3-25-26.pdf";

const WTBD = {
  intro: "Warrior Tasks and Battle Drills (WTBD) are the individual Soldier skills every recruit must master, organized around four fundamentals: Shoot, Move, Communicate, and Survive. Each task below links to its Army training manual and, where available, a demonstration video.",
  manuals: [
    { title: "TC 3-21.75 — The Warrior Ethos & Soldier Combat Skills", url: TC_3_21_75 },
    { title: "STP 21-1-SMCT — Soldier's Manual of Common Tasks (Warrior Skills Level 1)", url: STP_21_1 },
  ],
  categories: [
    {
      key: "shoot", label: "Shoot", icon: Crosshair,
      blurb: "Maintain and employ your assigned weapon accurately and safely.",
      items: [
        { task: "Maintain your assigned weapon (M4/M16)", manual: { label: "TC 3-21.75, Ch. 3", url: TC_3_21_75 }, video: yt("US Army M4 carbine disassembly cleaning maintenance") },
        { task: "Perform a function check on an M4/M16", manual: { label: "STP 21-1-SMCT 071-COM-0030", url: STP_21_1 }, video: yt("US Army M4 function check") },
        { task: "Load, reduce a stoppage, and clear an M4/M16", manual: { label: "TC 3-21.75, Ch. 3", url: TC_3_21_75 }, video: yt("US Army M4 immediate action SPORTS stoppage") },
        { task: "Engage targets with an M4/M16 carbine", manual: { label: "STP 21-1-SMCT 071-COM-0025", url: STP_21_1 }, video: yt("US Army rifle marksmanship engage targets fundamentals") },
        { task: "Employ hand grenades (M67)", manual: { label: "TC 3-21.75, Ch. 3", url: TC_3_21_75 }, video: yt("US Army employ M67 hand grenade") },
      ],
    },
    {
      key: "move", label: "Move", icon: Footprints,
      blurb: "Move tactically, react to fire, and navigate the terrain.",
      items: [
        { task: "Move under direct fire", manual: { label: "STP 21-1-SMCT 071-COM-0502", url: STP_21_1 }, video: yt("US Army move under direct fire 3-5 second rush") },
        { task: "React to indirect fire while dismounted", manual: { label: "STP 21-1-SMCT 071-COM-0501", url: STP_21_1 }, video: yt("US Army react to indirect fire") },
        { task: "Move over, through, or around obstacles (except minefields)", manual: { label: "TC 3-21.75, Ch. 5", url: TC_3_21_75 }, video: yt("US Army high crawl low crawl obstacles") },
        { task: "Navigate from one point to another (land navigation)", manual: { label: "FM 3-25.26 Map Reading & Land Nav", url: FM_LAND_NAV }, video: yt("US Army land navigation map reading basics") },
        { task: "Select temporary fighting positions", manual: { label: "TC 3-21.75, Ch. 5", url: TC_3_21_75 }, video: yt("US Army individual fighting position") },
      ],
    },
    {
      key: "communicate", label: "Communicate", icon: Radio,
      blurb: "Pass clear, accurate information by radio, voice, and signal.",
      items: [
        { task: "Send a radio message (SINCGARS/ASIP)", manual: { label: "STP 21-1-SMCT 113-COM-2022", url: STP_21_1 }, video: yt("US Army send radio message prowords") },
        { task: "Use visual signaling techniques (hand & arm signals)", manual: { label: "TC 3-21.75, Ch. 6", url: TC_3_21_75 }, video: yt("US Army hand and arm signals") },
        { task: "Send a SALUTE / SPOT report", manual: { label: "STP 21-1-SMCT 301-371-1000", url: STP_21_1 }, video: yt("US Army SALUTE report SPOT report") },
        { task: "Use the phonetic alphabet & prowords", manual: { label: "TC 3-21.75, Ch. 6", url: TC_3_21_75 }, video: yt("US Army phonetic alphabet radio prowords") },
      ],
    },
    {
      key: "survive", label: "Survive", icon: HeartPulse,
      blurb: "Keep yourself and your battle buddy alive under any condition.",
      items: [
        { task: "Evaluate a casualty (Tactical Combat Casualty Care)", manual: { label: "STP 21-1-SMCT 081-COM-1001", url: STP_21_1 }, video: yt("US Army evaluate a casualty TCCC MARCH") },
        { task: "Apply a tourniquet to control bleeding", manual: { label: "TC 3-21.75, Ch. 7", url: TC_3_21_75 }, video: yt("US Army apply CAT tourniquet") },
        { task: "Perform first aid for an open wound / shock", manual: { label: "STP 21-1-SMCT 081-COM-1005", url: STP_21_1 }, video: yt("US Army first aid open wound treat for shock") },
        { task: "React to a chemical attack & don your protective mask", manual: { label: "TC 3-21.75, Ch. 8", url: TC_3_21_75 }, video: yt("US Army don protective mask react to chemical attack") },
        { task: "Decontaminate yourself & individual equipment (CBRN)", manual: { label: "STP 21-1-SMCT 031-COM-1010", url: STP_21_1 }, video: yt("US Army individual decontamination CBRN") },
      ],
    },
  ],
};

function WarriorTasks() {
  return (
    <div className="space-y-5" data-testid="wtbd-content">
      <div className="rsp-card p-5">
        <div className="flex items-center gap-2 mb-2"><ShieldAlert className="gold" size={22} /><span className="font-head uppercase tracking-wide text-lg">Shoot · Move · Communicate · Survive</span></div>
        <p className="text-[var(--rsp-gray)] leading-relaxed">{WTBD.intro}</p>
      </div>

      <section>
        <div className="eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>Primary Manuals</div>
        <div className="space-y-2">
          {WTBD.manuals.map((m, i) => (
            <a key={m.url} href={m.url} target="_blank" rel="noreferrer" data-testid={`wtbd-manual-${i}`}
              className="rsp-card p-3 flex items-center gap-3 hover:border-[var(--rsp-gold)]/60 transition-colors">
              <BookOpen className="gold shrink-0" size={18} /><span className="flex-1 text-sm">{m.title}</span><ExternalLink size={15} className="text-[var(--rsp-muted)]" />
            </a>
          ))}
        </div>
      </section>

      {WTBD.categories.map((cat) => (
        <section key={cat.key} data-testid={`wtbd-cat-${cat.key}`}>
          <div className="flex items-center gap-2 mb-1">
            <cat.icon className="gold" size={20} />
            <h3 className="font-head uppercase tracking-wide text-lg">{cat.label}</h3>
          </div>
          <p className="text-[var(--rsp-muted)] text-xs mb-3">{cat.blurb}</p>
          <div className="space-y-2">
            {cat.items.map((it, i) => (
              <div key={it.task} className="rsp-card p-3" data-testid={`wtbd-${cat.key}-${i}`}>
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle2 className="gold shrink-0 mt-0.5" size={16} />
                  <span className="text-sm font-medium">{it.task}</span>
                </div>
                <div className="flex flex-wrap gap-2 pl-6">
                  <a href={it.manual.url} target="_blank" rel="noreferrer" data-testid={`wtbd-${cat.key}-${i}-manual`}
                    className="text-xs px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)] hover:border-[var(--rsp-gold)] flex items-center gap-1.5 transition-colors">
                    <FileText size={13} /> {it.manual.label}
                  </a>
                  {it.video && (
                    <a href={it.video} target="_blank" rel="noreferrer" data-testid={`wtbd-${cat.key}-${i}-video`}
                      className="text-xs px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)] hover:border-[var(--rsp-gold)] flex items-center gap-1.5 transition-colors">
                      <PlayCircle size={13} /> Video
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

const STRIPES = {
  intro: "Stripes for Skills lets Future Soldiers ship to Basic at an advanced rank (up to PFC/E-3, or SPC/E-4 with a degree). Earn promotion points before you ship by completing referrals, fitness, education, and Army knowledge tasks — more stripes means more pay on day one.",
  earn: [
    "Refer a friend who enlists (Army referral program)",
    "Score high on the ACFT / pass the fitness assessment",
    "Complete Future Soldier Training Program (FSTP) modules",
    "Earn college credit hours, JROTC (2–3 yrs), or Eagle Scout / Gold Award",
    "Memorize and recite required Soldier knowledge at drill",
  ],
  learn: [
    "The Soldier's Creed",
    "The three General Orders",
    "Rank structure (enlisted, warrant, officer)",
    "The phonetic alphabet (Alpha, Bravo, Charlie…)",
    "Chain of command & Army values (LDRSHIP)",
    "ACFT events and standards",
  ],
  links: [
    { title: "GoArmy — Future Soldiers", url: "https://www.goarmy.com/futuresoldiers.html" },
    { title: "Army Values (LDRSHIP)", url: "https://www.army.mil/values/" },
    { title: "ACFT Standards", url: "https://www.army.mil/acft/" },
    { title: "MyRSP Portal", url: "https://www.myrsp.army.mil/" },
  ],
  videos: [
    { title: "Soldier's Creed (recite along)", url: "https://www.youtube.com/watch?v=yG26LqHLwVY" },
    { title: "Army Three General Orders explained", url: "https://www.youtube.com/watch?v=ZNsUgV8Hc04" },
    { title: "Army rank structure explained", url: "https://www.youtube.com/watch?v=4R8TebiCrOU" },
    { title: "ACFT event walkthrough", url: "https://www.youtube.com/watch?v=IqzWzRNcd6M" },
  ],
};

function StripesForSkills() {
  return (
    <div className="space-y-5" data-testid="stripes-content">
      <div className="rsp-card p-5">
        <div className="flex items-center gap-2 mb-2"><Award className="gold" size={22} /><span className="font-head uppercase tracking-wide text-lg">Earn Rank Before You Ship</span></div>
        <p className="text-[var(--rsp-gray)] leading-relaxed">{STRIPES.intro}</p>
      </div>
      <section>
        <div className="eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>How to Earn Stripes</div>
        <div className="space-y-2">
          {STRIPES.earn.map((t, i) => (
            <div key={t} className="rsp-card p-3 flex items-start gap-2" data-testid={`stripes-earn-${i}`}>
              <CheckCircle2 className="gold shrink-0 mt-0.5" size={16} /><span className="text-sm">{t}</span>
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>What to Learn</div>
        <div className="rsp-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {STRIPES.learn.map((t) => (
            <div key={t} className="flex items-center gap-2 text-sm text-[var(--rsp-gray)]"><span className="w-1.5 h-1.5 rounded-full bg-[var(--rsp-gold)]" /> {t}</div>
          ))}
        </div>
      </section>
      <section>
        <div className="eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>Videos</div>
        <div className="space-y-2">
          {STRIPES.videos.map((v, i) => (
            <a key={v.url || v.title} href={v.url} target="_blank" rel="noreferrer" data-testid={`stripes-video-${i}`}
              className="rsp-card p-3 flex items-center gap-3 hover:border-[var(--rsp-gold)]/60">
              <PlayCircle className="gold shrink-0" size={20} /><span className="flex-1 text-sm">{v.title}</span><ExternalLink size={15} className="text-[var(--rsp-muted)]" />
            </a>
          ))}
        </div>
      </section>
      <section>
        <div className="eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>Helpful Links</div>
        <div className="space-y-2">
          {STRIPES.links.map((l, i) => (
            <a key={l.url || l.title} href={l.url} target="_blank" rel="noreferrer" data-testid={`stripes-link-${i}`}
              className="rsp-card p-3 flex items-center gap-3 hover:border-[var(--rsp-gold)]/60">
              <Link2 className="gold shrink-0" size={18} /><span className="flex-1 text-sm">{l.title}</span><ExternalLink size={15} className="text-[var(--rsp-muted)]" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

function Row({ item, icon: Icon, sub }) {
  const inner = (
    <div className="rsp-card p-4 flex items-center gap-3 hover:border-[var(--rsp-gold)]/60 transition-colors">
      <Icon className="gold shrink-0" size={20} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{item.code ? `${item.code} — ${item.title}` : item.title}</div>
        {sub && <div className="text-[var(--rsp-gray)] text-sm line-clamp-2">{sub}</div>}
      </div>
      {item.url && <ExternalLink size={16} className="text-[var(--rsp-muted)] shrink-0" />}
    </div>
  );
  return item.url ? (
    <a href={item.url} target="_blank" rel="noreferrer" data-testid={`resource-${item.id}`}>{inner}</a>
  ) : <div data-testid={`resource-${item.id}`}>{inner}</div>;
}

function List({ items, icon, subKey }) {
  if (items.length === 0) return <p className="text-[var(--rsp-muted)]">Nothing here yet.</p>;
  return (
    <div className="space-y-3">
      {items.map((it) => <Row key={it.id} item={it} icon={icon} sub={it[subKey]} />)}
    </div>
  );
}

const TABS = [
  { value: "bct", label: "BCT Sites", icon: MapPin },
  { value: "websites", label: "Websites", icon: Link2 },
  { value: "regulations", label: "Regs", icon: FileText },
  { value: "wtbd", label: "WTBD", icon: ShieldAlert },
  { value: "stripes", label: "Stripes", icon: Award },
];

export default function Resources() {
  const [regs, setRegs] = useState([]);
  const [links, setLinks] = useState([]);
  const [bct, setBct] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/regulations").then((r) => setRegs(r.data)).catch(() => {});
    api.get("/websites").then((r) => setLinks(r.data)).catch(() => {});
    api.get("/bct").then((r) => setBct(r.data)).catch(() => {});
  }, []);

  const match = (it) => {
    const s = q.toLowerCase();
    return [it.title, it.code, it.description, it.summary, it.url].filter(Boolean).some((v) => v.toLowerCase().includes(s));
  };
  const fReg = regs.filter(match), fLinks = links.filter(match), fBct = bct.filter(match);

  return (
    <div data-testid="resources-page">
      <div className="eyebrow mb-1" style={{ letterSpacing: "0.16em" }}>Reference Library</div>
      <h1 className="heading text-4xl mb-4">Resources</h1>

      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" size={16} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search resources…" data-testid="resources-search"
          className="w-full rsp-card pl-9 pr-4 py-3 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)]" />
      </div>

      <Tabs defaultValue="bct">
        <TabsList className="grid grid-cols-3 sm:grid-cols-5 w-full gap-2 bg-transparent p-0 h-auto mb-5">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`resources-tab-${t.value}`}
              className="w-full min-w-0 px-1.5 py-2.5 rounded-lg border border-[var(--rsp-border)] font-head uppercase tracking-wide text-[11px] flex items-center justify-center gap-1 data-[state=active]:bg-[var(--rsp-gold)] data-[state=active]:text-black data-[state=active]:border-[var(--rsp-gold)]">
              <t.icon size={14} className="shrink-0" /> <span className="truncate">{t.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="bct"><List items={fBct} icon={MapPin} subKey="description" /></TabsContent>
        <TabsContent value="websites"><List items={fLinks} icon={Link2} subKey="description" /></TabsContent>
        <TabsContent value="regulations"><List items={fReg} icon={FileText} subKey="summary" /></TabsContent>
        <TabsContent value="wtbd"><WarriorTasks /></TabsContent>
        <TabsContent value="stripes"><StripesForSkills /></TabsContent>
      </Tabs>
    </div>
  );
}
